import { request } from '@umijs/max';

const TOKEN_PLATFORM_PREFIX = '/bff/token-platform';

// WIS-496: token-platform (new-api) 凭证统一收口到 @/utils/token-platform-auth，
// 保证登出/换号/session 失效时一致清理，并在使用前校验归属（owner）防跨账号串号。
// 本地 import 供 service 内部（newApiHeaders）使用，同时 re-export 供页面使用。
import {
  clearTokenPlatformCredentials,
  getTokenPlatformOwner,
  getTokenPlatformToken,
  getTokenPlatformUserId,
  setTokenPlatformOwner,
  setTokenPlatformToken,
  setTokenPlatformUserId,
} from '@/utils/token-platform-auth';

export {
  clearTokenPlatformCredentials,
  getTokenPlatformOwner,
  getTokenPlatformToken,
  getTokenPlatformUserId,
  setTokenPlatformOwner,
  setTokenPlatformToken,
  setTokenPlatformUserId,
};

const TOKEN_PLATFORM_URL_KEY = 'wischoicer_token_platform_base_url';

export const getTokenPlatformBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(TOKEN_PLATFORM_URL_KEY) || '';
};

export const setTokenPlatformBaseUrl = (url: string) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_PLATFORM_URL_KEY, url);
};

// ---- API Response Types ----

export interface TokenPlatformLoginResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type: string;
  user?: {
    id: number;
    email: string;
    username?: string;
  };
}

export interface TokenPlatformTotpRequiredResponse {
  requires_2fa: boolean;
  temp_token: string;
  email: string;
}

export type LoginResult =
  | TokenPlatformLoginResponse
  | TokenPlatformTotpRequiredResponse;

export function isTotpRequired(
  res: LoginResult,
): res is TokenPlatformTotpRequiredResponse {
  return 'requires_2fa' in res && res.requires_2fa === true;
}

// new-api returns {success, message, data}
interface NewApiEnvelope<T> {
  success: boolean;
  message: string;
  data?: T;
}

// Legacy envelope for login endpoints behind BFF proxy
interface LegacyEnvelope<T> {
  code: number;
  message: string;
  data?: T;
}

function unwrapNewApi<T>(res: NewApiEnvelope<T>): T {
  if (!res.success || res.data === undefined) {
    const err: any = new Error(
      res.message || 'Unknown error from token platform',
    );
    err.isNewApiError = true;
    throw err;
  }
  return res.data;
}

function unwrapLegacy<T>(res: LegacyEnvelope<T>): T {
  if (res.code !== 0 || !res.data) {
    throw new Error(res.message || 'Unknown error from token platform');
  }
  return res.data;
}

// ---- API Functions ----

export async function getTokenPlatformConfig(): Promise<{
  frontend_url: string;
}> {
  const res = await request<{ frontend_url: string }>(
    `${TOKEN_PLATFORM_PREFIX}/config`,
    {
      method: 'GET',
      skipErrorHandler: true,
    },
  );
  return res;
}

export async function getSsoUrl(redirect: string = '/'): Promise<{
  success: boolean;
  message: string;
  sso_url: string;
  access_token: string;
  new_api_user_id?: number | null;
  redirect: string;
}> {
  const res = await request<{
    success: boolean;
    message: string;
    sso_url: string;
    access_token: string;
    new_api_user_id?: number | null;
    redirect: string;
  }>(`/bff/gateway/user/token-platform/sso-url`, {
    method: 'POST',
    params: { redirect },
    skipErrorHandler: true,
  });
  return res;
}

export async function exchangeTokenPlatformToken(): Promise<{
  success: boolean;
  message: string;
  access_token: string;
  new_api_user_id?: number | null;
}> {
  const res = await request<{
    success: boolean;
    message: string;
    access_token: string;
    new_api_user_id?: number | null;
  }>(`/bff/gateway/user/token-platform/exchange-token`, {
    method: 'POST',
    skipErrorHandler: true,
  });
  return res;
}

export async function loginTokenPlatform(
  account: string,
  password: string,
): Promise<LoginResult> {
  const res = await request<LegacyEnvelope<LoginResult>>(
    `${TOKEN_PLATFORM_PREFIX}/api/v1/auth/login`,
    {
      method: 'POST',
      data: { account, password },
      skipErrorHandler: true,
    },
  );
  return unwrapLegacy(res);
}

export async function loginTokenPlatform2FA(
  tempToken: string,
  code: string,
): Promise<TokenPlatformLoginResponse> {
  const res = await request<LegacyEnvelope<TokenPlatformLoginResponse>>(
    `${TOKEN_PLATFORM_PREFIX}/api/v1/auth/login/2fa`,
    {
      method: 'POST',
      data: { temp_token: tempToken, totp_code: code },
      skipErrorHandler: true,
    },
  );
  return unwrapLegacy(res);
}

/**
 * Build auth headers for new-api requests: Authorization + X-Token-Platform-User-Id
 * BFF proxy maps X-Token-Platform-User-Id → New-Api-User required by new-api UserAuth().
 */
function newApiHeaders(token: string): Record<string, string> {
  // SSO 会重新生成 new-api PAT。组件闭包里的 token 可能已陈旧，因此每次请求优先读取
  // 由 SSO/重连流程原子更新的最新凭证，避免旧 PAT 401 后误触发主站登出。
  const latestToken = getTokenPlatformToken();
  const headers: Record<string, string> = {
    Authorization: latestToken || token,
  };
  const userId = getTokenPlatformUserId();
  if (userId) {
    headers['X-Token-Platform-User-Id'] = userId;
  }
  return headers;
}

/**
 * Fetch current user's balance from new-api: GET /api/user/self
 * Returns { quota, used_quota } for balance calculation.
 */
export async function getUserBalance(
  token: string,
): Promise<{ quota: number; used_quota: number }> {
  const res = await request<
    NewApiEnvelope<{ quota: number; used_quota: number }>
  >(`${TOKEN_PLATFORM_PREFIX}/api/user/self`, {
    headers: {
      ...newApiHeaders(token),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    params: { _balanceRefresh: Date.now() },
    skipErrorHandler: true,
  });
  const data = unwrapNewApi(res);
  return {
    quota: data.quota ?? 0,
    used_quota: data.used_quota ?? 0,
  };
}

// ---- Feature Usage（WIS-499 §4 接口契约 / WIS-502 new-api 实现）----
// 走现有 /bff/token-platform 透明代理；BFF 仅映射 X-Token-Platform-User-Id →
// New-Api-User，不参与业务归因计算。前端不二次聚合 summary/tasks。

/** RFC §3.1 — 本期启用的 feature_code 枚举（预留 copywriting_creation / video_creation） */
export const FEATURE_CODE_OPTIONS = [
  { value: 'reference_copy', label: '爆款复刻中心 - 图文爆款复刻' },
  { value: 'merch_video_clone', label: '爆款复刻中心 - 视频爆款复刻' },
  { value: 'image_creation', label: '爆款内容工坊 - 图片创作' },
] as const;

export const FEATURE_CODE_LABEL: Record<string, string> = Object.fromEntries(
  FEATURE_CODE_OPTIONS.map((o) => [o.value, o.label]),
);

/** RFC §4 公共：时间窗口（Unix 秒，闭区间，单次 ≤ 92 天） */
export interface FeatureUsageRange {
  start_timestamp: number;
  end_timestamp: number;
}

export interface FeatureUsageTotals {
  quota: number;
  cost_rmb: number;
  request_count: number;
}

export interface FeatureUsageFeatureAgg {
  feature_code: string;
  feature_name: string;
  task_count: number;
  request_count: number;
  quota: number;
  cost_rmb: number;
  first_seen: number; // Unix 秒
  last_seen: number; // Unix 秒
}

export interface FeatureUsageUncategorized {
  present: boolean;
  quota: number;
  cost_rmb: number;
  request_count: number;
}

export interface FeatureUsageSummary {
  totals: FeatureUsageTotals;
  features: FeatureUsageFeatureAgg[];
  uncategorized: FeatureUsageUncategorized;
}

export interface FeatureUsageTaskAgg {
  biz_task_id: string;
  biz_task_title: string;
  feature_code: string;
  feature_name: string;
  request_count: number;
  quota: number;
  cost_rmb: number;
  first_seen: number;
  last_seen: number;
}

export interface FeatureUsageTasksResult {
  page: number;
  page_size: number;
  total: number;
  items: FeatureUsageTaskAgg[];
}

export interface FeatureUsageDetailItem {
  created_at: number; // Unix 秒
  feature_code: string;
  feature_name: string;
  biz_task_id: string;
  biz_task_title: string;
  operation_code: string;
  operation_name: string;
  sub_task_id: string;
  billing_stage: string; // request | submit | settle | refund
  log_type: string; // consume | refund
  token_name: string;
  token_id: number;
  quota: number; // 已带正负号（consume 正 / refund 负）
  cost_rmb: number;
  prompt_tokens: number;
  completion_tokens: number;
  // WIS-514：model_name / request_id / upstream_request_id 已从客户侧 self 响应裁剪，
  // 不再出现在该类型；后端原始日志照常记录，仅供内部排障与归因闭环。
  provider_task_id: string | null;
}

export interface FeatureUsageDetailsResult {
  page: number;
  page_size: number;
  total: number;
  items: FeatureUsageDetailItem[];
}

/** 去除空串 / undefined / null，避免把它们当真实 query 发出 */
function cleanParams(
  obj: Record<string, unknown>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v as string | number;
  }
  return out;
}

/** 公共：summary / tasks / details 三层共用的过滤参数（WIS-514 方案 A：三层口径一致）。 */
export interface FeatureUsageFilters {
  feature_code?: string;
  biz_task_id?: string;
  task_keyword?: string;
  operation_code?: string;
}

/**
 * WIS-499 §4.1 — GET /api/log/self/feature-usage/summary
 * 聚合总费用 + 按功能板块 + uncategorized。feature_code 不允许传 uncategorized。
 * WIS-514 方案 A：summary 与 tasks/details 同口径，支持 biz_task_id / task_keyword / operation_code，
 * 使顶部汇总受全局筛选影响（无匹配时三层都为空 / 费用为 0）。
 */
export async function getFeatureUsageSummary(
  token: string,
  range: FeatureUsageRange,
  filters: FeatureUsageFilters = {},
): Promise<FeatureUsageSummary> {
  const res = await request<NewApiEnvelope<FeatureUsageSummary>>(
    `${TOKEN_PLATFORM_PREFIX}/api/log/self/feature-usage/summary`,
    {
      headers: newApiHeaders(token),
      params: cleanParams({
        ...range,
        ...filters,
      }),
      skipErrorHandler: true,
    },
  );
  return unwrapNewApi(res);
}

/**
 * WIS-499 §4.2 — GET /api/log/self/feature-usage/tasks
 * 聚合键 feature_code + biz_task_id；不支持 uncategorized。
 */
export async function getFeatureUsageTasks(
  token: string,
  range: FeatureUsageRange,
  filters: FeatureUsageFilters,
  page = 1,
  pageSize = 20,
): Promise<FeatureUsageTasksResult> {
  const res = await request<NewApiEnvelope<FeatureUsageTasksResult>>(
    `${TOKEN_PLATFORM_PREFIX}/api/log/self/feature-usage/tasks`,
    {
      headers: newApiHeaders(token),
      params: cleanParams({
        ...range,
        ...filters,
        p: page,
        page_size: pageSize,
      }),
      skipErrorHandler: true,
    },
  );
  return unwrapNewApi(res);
}

/**
 * WIS-499 §4.3 — GET /api/log/self/feature-usage/details
 * 明细；允许传 uncategorized。provider_task_id 可为 null。
 * WIS-514：客户侧 self 响应已裁剪 model_name / request_id / upstream_request_id，
 * 详见 FeatureUsageDetailItem（不再含这三字段）。
 */
export async function getFeatureUsageDetails(
  token: string,
  range: FeatureUsageRange,
  filters: FeatureUsageFilters,
  page = 1,
  pageSize = 20,
): Promise<FeatureUsageDetailsResult> {
  const res = await request<NewApiEnvelope<FeatureUsageDetailsResult>>(
    `${TOKEN_PLATFORM_PREFIX}/api/log/self/feature-usage/details`,
    {
      headers: newApiHeaders(token),
      params: cleanParams({
        ...range,
        ...filters,
        p: page,
        page_size: pageSize,
      }),
      skipErrorHandler: true,
    },
  );
  return unwrapNewApi(res);
}

// ---- 充值（billing）WIS-625 ----
// 走 /bff/gateway/billing/**（PR #133 已接通 → apigateway → billing）。
// 鉴权由 umi 全局请求拦截器自动注入 Wischoicer JWT + user_id（BFF 仅透传，不验签、
// 不持 X-Gateway-Token）；因此这里与 token-platform 的 new-api JWT 链路不同：
// 不传 token 参数、不加 newApiHeaders。

const BILLING_GATEWAY_PREFIX = '/bff/gateway/billing';

/**
 * 对外聚合订单状态，与 billing backend AggregateStatus 严格对齐。
 * 后端把 CREATING/RECOVERING/PENDING/CLOSING 全部折叠为 PENDING_PAYMENT。
 * - PENDING_PAYMENT：准备二维码 / 等待扫码 / 确认支付结果（含恢复中）
 * - CREDITING：支付成功，到账中
 * - SUCCESS：充值完成（终态）
 * - CLOSED：订单关闭（终态）
 * - CREDIT_FAILED：支付成功但自动到账失败，后台处理中（终态）
 */
export type BillingOrderStatus =
  | 'PENDING_PAYMENT'
  | 'CREDITING'
  | 'SUCCESS'
  | 'CLOSED'
  | 'CREDIT_FAILED';

export interface CreateBillingOrderRequest {
  /** 用户维度幂等键；同一 userId + clientRequestId 复用同一订单。ASCII [A-Za-z0-9_-]，≤64 字符。 */
  clientRequestId: string;
  amountCents: number;
}

export interface BillingOrderSnapshot {
  orderNo: string;
  amountCents: number;
  /** 微信实际收取金额；活动订单小于 amountCents。 */
  payAmountCents: number;
  /** 10000=原价，9000=支付90%。 */
  discountBasisPoints: number;
  promotionName?: string | null;
  promotionStartAt?: string | null;
  promotionEndAt?: string | null;
  currency: string;
  /** 到账额度（内部 quota 单位）。RED LINE：禁止直接展示给用户（仅用于 SUCCESS 后余额刷新的内部数据流）。 */
  quota: number;
  status: BillingOrderStatus;
  /** CLOSED 时的关闭原因；USER_CANCELLED 表示用户主动取消。 */
  closeReason?: string | null;
  /** Native 下单的 code_url；非有效 PENDING 状态返回 null，禁止展示已关闭二维码。 */
  codeUrl: string | null;
  expireTime: string | null;
  paidTime?: string | null;
  createTime?: string | null;
}

export interface BillingOrderListResult {
  items: BillingOrderSnapshot[];
  nextCursor?: string | null;
}

export interface RechargePromotion {
  campaignName: string;
  discountBasisPoints: number;
  startAt?: string | null;
  endAt?: string | null;
  updatedAt?: string | null;
  active: boolean;
  presetAmountCents: number[];
  minAmountCents: number;
  maxAmountCents: number;
}

export async function getRechargePromotion(): Promise<RechargePromotion> {
  return request<RechargePromotion>(
    `${BILLING_GATEWAY_PREFIX}/recharge-promotion`,
    {
      method: 'GET',
      params: { _promotionRefresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      skipErrorHandler: true,
    },
  );
}

/**
 * 创建微信支付订单。服务端开关关闭（BILLING_CREATE_ORDER_ENABLED=false）时
 * 抛出 503 / code=`CREATE_ORDER_DISABLED`。POST 可能返回 202（Recovering），
 * 响应体同为 BillingOrderSnapshot，客户端按同一 clientRequestId 轮询即可。
 */
export async function createBillingOrder(
  payload: CreateBillingOrderRequest,
): Promise<BillingOrderSnapshot> {
  return request<BillingOrderSnapshot>(`${BILLING_GATEWAY_PREFIX}/orders`, {
    method: 'POST',
    data: payload,
    skipErrorHandler: true,
  });
}

/** 查询单笔订单状态（只返回当前用户的订单）。404 → ORDER_NOT_FOUND。 */
export async function getBillingOrder(
  orderNo: string,
): Promise<BillingOrderSnapshot> {
  return request<BillingOrderSnapshot>(
    `${BILLING_GATEWAY_PREFIX}/orders/${encodeURIComponent(orderNo)}`,
    {
      method: 'GET',
      skipErrorHandler: true,
    },
  );
}

/** 查询当前用户的充值支付订单，按创建时间倒序游标分页。 */
export async function listBillingOrders(
  cursor?: string,
  limit = 20,
): Promise<BillingOrderListResult> {
  return request<BillingOrderListResult>(`${BILLING_GATEWAY_PREFIX}/orders`, {
    method: 'GET',
    params: cleanParams({ cursor, limit }),
    skipErrorHandler: true,
  });
}

/** 主动取消未支付订单；若微信关单仍在确认，后端返回最新待支付快照。 */
export async function cancelBillingOrder(
  orderNo: string,
): Promise<BillingOrderSnapshot> {
  return request<BillingOrderSnapshot>(
    `${BILLING_GATEWAY_PREFIX}/orders/${encodeURIComponent(orderNo)}/cancel`,
    {
      method: 'POST',
      skipErrorHandler: true,
    },
  );
}

/**
 * 从 axios 错误中读取 billing 错误码（response.data.code）。
 * billing 错误信封是 { code, message }（非 { success:false }），umi 的 errorThrower
 * 不会触发；调用方需 skipErrorHandler:true 后自行读取。
 */
export function getBillingErrorCode(err: unknown): string | undefined {
  const e = err as { response?: { data?: { code?: string } } };
  return e?.response?.data?.code;
}

/** 从 axios 错误中读取 HTTP 状态码（用于识别 401 会话过期 / 404 订单不存在等）。 */
export function getBillingErrorStatus(err: unknown): number | undefined {
  const e = err as { response?: { status?: number } };
  return e?.response?.status;
}

// ---- Feature Usage Analytics（WIS-523 §建议后端实现 / new-api 已合入 develop）----
// 概览页「功能模块消耗分析」数据源；替代旧 /api/data/self 单线趋势与恒 0 的
// /api/log/self/stat。口径与 summary 同源（净额 / request_count 只计 request·submit /
// cost_rmb=quota/500000），前端不二次聚合，只做图表数据转换（featureCostChartData.ts）。

/** 后端 hour|day|week 桶聚合粒度；不传 time_granularity 时后端按窗口自动推断。 */
export type FeatureUsageTimeGranularity = 'hour' | 'day' | 'week';

export interface FeatureUsageAnalyticsTotals {
  quota: number; // 净额（consume 正 / refund 负），与 summary.totals 同口径
  cost_rmb: number; // quota / 500000
  request_count: number; // 仅 billing_stage ∈ (request, submit)
  token_used: number; // prompt + completion，仅 consume 行计入
  avg_rpm: number; // request_count / 窗口分钟数
  avg_tpm: number; // token_used / 窗口分钟数
}

export interface FeatureUsageAnalyticsFeature {
  feature_code: string; // 「其他功能消耗」行为空串——堆叠图须按 feature_name 分系列
  feature_name: string;
  quota: number;
  cost_rmb: number;
  request_count: number;
  token_used: number;
  first_seen: number; // Unix 秒
  last_seen: number; // Unix 秒
}

export interface FeatureUsageAnalyticsPoint {
  bucket_ts: number; // UTC 桶起点 Unix 秒，前端按本地时区格式化
  bucket_label: string; // 后端兜底标签（hour: MM-DD HH:mm；day/week: MM-DD）
  feature_code: string;
  feature_name: string;
  quota: number;
  cost_rmb: number;
  request_count: number;
  token_used: number;
}

export interface FeatureUsageAnalyticsResult {
  totals: FeatureUsageAnalyticsTotals;
  features: FeatureUsageAnalyticsFeature[];
  points: FeatureUsageAnalyticsPoint[];
  /** 后端实际生效的聚合粒度（自动推断时回填），前端据此格式化坐标轴/图例。 */
  granularity: FeatureUsageTimeGranularity;
}

/**
 * WIS-523 — GET /api/log/self/feature-usage/analytics
 * 功能模块消耗趋势/分布聚合。feature_code 为普通字符串匹配（不按枚举校验），
 * 支持动态/未来 feature 检索；未命中返回空结果。time_granularity 空 → 后端自动推断。
 * 响应绝不包含 model_name / request_id / upstream_request_id（后端裁剪）。
 */
export async function getFeatureUsageAnalytics(
  token: string,
  range: FeatureUsageRange,
  filters: FeatureUsageFilters = {},
  timeGranularity?: FeatureUsageTimeGranularity,
): Promise<FeatureUsageAnalyticsResult> {
  const res = await request<NewApiEnvelope<FeatureUsageAnalyticsResult>>(
    `${TOKEN_PLATFORM_PREFIX}/api/log/self/feature-usage/analytics`,
    {
      headers: newApiHeaders(token),
      params: cleanParams({
        ...range,
        ...filters,
        time_granularity: timeGranularity,
      }),
      skipErrorHandler: true,
    },
  );
  return unwrapNewApi(res);
}
